import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Lock, LogOut, RefreshCw, Inbox, FileText, ArrowRight, Trash2, Eye, X, ChevronLeft, ChevronRight } from "lucide-react";
import { Logo } from "../components/Logo";
import { fetchAdminEnquiries, fetchAdminCatalogRequests, updateEnquiryStatus, updateCatalogRequestStatus, deleteEnquiry, deleteCatalogRequest } from "../lib/api";
import { toast, Toaster } from "sonner";

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
};

export default function Admin() {
  const [key, setKey] = useState(() => sessionStorage.getItem("nuvun_admin_key") || "");
  const [authed, setAuthed] = useState(false);
  const [input, setInput] = useState("");
  const [tab, setTab] = useState("enquiries");
  const [enquiries, setEnquiries] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [viewItem, setViewItem] = useState(null); // { kind: 'enquiry'|'request', data: {...} }
  const PAGE_SIZE = 10;

  const load = useCallback(async (k) => {
    setLoading(true);
    try {
      const [e, r] = await Promise.all([fetchAdminEnquiries(k), fetchAdminCatalogRequests(k)]);
      setEnquiries(e.enquiries || []);
      setRequests(r.requests || []);
      setAuthed(true);
      sessionStorage.setItem("nuvun_admin_key", k);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Invalid access key.");
        sessionStorage.removeItem("nuvun_admin_key");
        setAuthed(false);
      } else {
        toast.error("Could not load leads. Try again.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (key) load(key);
  }, [key, load]);

  const login = (e) => {
    e.preventDefault();
    if (!input.trim()) return;
    setKey(input.trim());
  };

  const logout = () => {
    sessionStorage.removeItem("nuvun_admin_key");
    setKey("");
    setInput("");
    setAuthed(false);
    setEnquiries([]);
    setRequests([]);
  };

  const toggleStatus = async (enq) => {
    const next = enq.status === "contacted" ? "new" : "contacted";
    try {
      await updateEnquiryStatus(key, enq.id, next);
      setEnquiries((list) => list.map((e) => (e.id === enq.id ? { ...e, status: next } : e)));
      toast.success(`Marked as ${next}.`);
    } catch {
      toast.error("Could not update status.");
    }
  };

  const removeEnquiry = async (enq) => {
    if (!window.confirm(`Delete enquiry from ${enq.name}? This cannot be undone.`)) return;
    try {
      await deleteEnquiry(key, enq.id);
      setEnquiries((list) => list.filter((e) => e.id !== enq.id));
      toast.success("Enquiry deleted.");
    } catch {
      toast.error("Could not delete enquiry.");
    }
  };

  const removeRequest = async (r) => {
    if (!window.confirm(`Delete catalogue request from ${r.name}?`)) return;
    try {
      await deleteCatalogRequest(key, r.id);
      setRequests((list) => list.filter((x) => x.id !== r.id));
      toast.success("Request deleted.");
    } catch {
      toast.error("Could not delete request.");
    }
  };

  const toggleRequestStatus = async (r) => {
    const next = r.status === "contacted" ? "new" : "contacted";
    try {
      await updateCatalogRequestStatus(key, r.id, next);
      setRequests((list) => list.map((x) => (x.id === r.id ? { ...x, status: next } : x)));
      toast.success(next === "contacted" ? "Marked as sent." : "Reverted to new.");
    } catch {
      toast.error("Could not update status.");
    }
  };

  const newCount = enquiries.filter((e) => e.status !== "contacted").length;

  // Paginate whichever list is active
  const activeList = tab === "enquiries" ? enquiries : requests;
  const totalPages = Math.max(1, Math.ceil(activeList.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageStart = (currentPage - 1) * PAGE_SIZE;
  const pageItems = activeList.slice(pageStart, pageStart + PAGE_SIZE);

  const switchTab = (t) => { setTab(t); setPage(1); };

  return (
    <div className="min-h-screen bg-[#0A0908] text-[#F2EDE3]">
      <Toaster position="top-center" richColors />

      {/* Top bar */}
      <header className="border-b border-[#D9A441]/15 bg-[#0C0B09]">
        <div className="max-w-[1280px] mx-auto px-5 md:px-8 h-[68px] flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" data-testid="admin-logo-link"><Logo inverted height={30} /></Link>
            <span className="eyebrow text-[#D9A441] hidden sm:block">Leads Dashboard</span>
          </div>
          {authed && (
            <div className="flex items-center gap-3">
              <button onClick={() => load(key)} data-testid="admin-refresh" className="btn-ghost !px-4 !py-2.5">
                <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
              </button>
              <button onClick={logout} data-testid="admin-logout" className="btn-ghost !px-4 !py-2.5">
                <LogOut className="w-3.5 h-3.5" /> Exit
              </button>
            </div>
          )}
        </div>
      </header>

      {!authed ? (
        /* ---- Access gate ---- */
        <div className="flex items-center justify-center px-5 py-28">
          <form onSubmit={login} data-testid="admin-login-form" className="panel p-8 md:p-10 w-full max-w-sm text-center">
            <span className="mx-auto w-12 h-12 rounded-full border border-[#D9A441]/50 flex items-center justify-center mb-5">
              <Lock className="w-5 h-5 text-[#D9A441]" />
            </span>
            <div className="font-display text-2xl">Admin Access</div>
            <p className="text-xs text-[#F2EDE3]/50 mt-2 mb-6">Enter your access key to view leads.</p>
            <input
              type="password"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Access key"
              data-testid="admin-key-input"
              className="field text-center"
            />
            <button type="submit" disabled={loading} data-testid="admin-login-submit" className="btn-gold w-full mt-4 disabled:opacity-60">
              {loading ? "Checking…" : "Open Dashboard"} <ArrowRight className="w-4 h-4" />
            </button>
          </form>
        </div>
      ) : (
        /* ---- Dashboard ---- */
        <main className="max-w-[1280px] mx-auto px-5 md:px-8 py-10">
          {/* Stats */}
          <div className="grid grid-cols-3 gap-3 mb-8">
            {[
              ["Total Enquiries", enquiries.length, "admin-stat-enquiries"],
              ["New / Uncontacted", newCount, "admin-stat-new"],
              ["Catalogue Requests", requests.length, "admin-stat-requests"],
            ].map(([l, v, tid]) => (
              <div key={l} className="panel px-5 py-6 text-center" data-testid={tid}>
                <div className="font-condensed text-3xl md:text-4xl text-[#D9A441]">{v}</div>
                <div className="eyebrow !text-[9px] text-[#F2EDE3]/50 mt-2">{l}</div>
              </div>
            ))}
          </div>

          {/* Tabs */}
          <div className="flex items-center gap-2 mb-6">
            <button
              onClick={() => switchTab("enquiries")}
              data-testid="admin-tab-enquiries"
              className={`eyebrow inline-flex items-center gap-2 px-5 py-3 rounded-sm border transition-colors ${
                tab === "enquiries" ? "border-[#D9A441] bg-[#D9A441]/10 text-[#D9A441]" : "border-white/15 text-[#F2EDE3]/60 hover:border-white/40"
              }`}
            >
              <Inbox className="w-3.5 h-3.5" /> Enquiries ({enquiries.length})
            </button>
            <button
              onClick={() => switchTab("requests")}
              data-testid="admin-tab-requests"
              className={`eyebrow inline-flex items-center gap-2 px-5 py-3 rounded-sm border transition-colors ${
                tab === "requests" ? "border-[#D9A441] bg-[#D9A441]/10 text-[#D9A441]" : "border-white/15 text-[#F2EDE3]/60 hover:border-white/40"
              }`}
            >
              <FileText className="w-3.5 h-3.5" /> Catalogue Requests ({requests.length})
            </button>
          </div>

          {/* Table */}
          <div className="panel overflow-x-auto" data-testid={`admin-table-${tab}`}>
            {tab === "enquiries" ? (
              <table className="w-full text-left text-sm min-w-[980px]">
                <thead>
                  <tr className="border-b border-[#D9A441]/15 text-[#D9A441]">
                    {["Date", "Name", "Actions", "Contact", "Type", "Product / Interest", "Segment", "Budget", "Timeline", "Message"].map((h) => (
                      <th key={h} className="eyebrow !text-[9px] px-4 py-4 whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {enquiries.length === 0 && (
                    <tr><td colSpan={10} className="px-4 py-10 text-center text-[#F2EDE3]/40">No enquiries yet.</td></tr>
                  )}
                  {pageItems.map((e) => (
                    <tr key={e.id} data-testid={`admin-enquiry-row-${e.id}`} className="border-b border-white/5 hover:bg-white/[0.025] align-top">
                      <td className="px-4 py-4 whitespace-nowrap text-[#F2EDE3]/55 text-xs">{fmtDate(e.created_at)}</td>
                      <td className="px-4 py-4 font-semibold whitespace-nowrap">{e.name}</td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => setViewItem({ kind: "enquiry", data: e })}
                            data-testid={`admin-view-enquiry-${e.id}`}
                            title="View full details"
                            className="p-2 rounded-sm border border-[#D9A441]/40 text-[#D9A441] hover:bg-[#D9A441]/10 transition-colors"
                          >
                            <Eye className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => toggleStatus(e)}
                            data-testid={`admin-status-toggle-${e.id}`}
                            className={`eyebrow !text-[9px] px-3 py-1.5 rounded-sm border transition-colors whitespace-nowrap ${
                              e.status === "contacted"
                                ? "border-[#4ade80]/40 text-[#4ade80]"
                                : "border-[#D9A441] bg-[#D9A441]/10 text-[#D9A441]"
                            }`}
                          >
                            {e.status === "contacted" ? "Contacted ✓" : "Mark done"}
                          </button>
                          <button
                            onClick={() => removeEnquiry(e)}
                            data-testid={`admin-delete-enquiry-${e.id}`}
                            title="Delete enquiry"
                            className="p-2 rounded-sm border border-white/10 text-[#F2EDE3]/50 hover:border-red-500/60 hover:text-red-400 transition-colors"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                      <td className="px-4 py-4 text-xs">
                        <a href={`mailto:${e.email}`} className="text-[#D9A441] hover:underline block">{e.email}</a>
                        {e.phone && <span className="text-[#F2EDE3]/55">{e.phone}</span>}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className="eyebrow !text-[9px] border border-[#D9A441]/30 text-[#F2EDE3]/70 px-2.5 py-1 rounded-sm">{e.enquiry_type}</span>
                      </td>
                      <td className="px-4 py-4 text-xs text-[#F2EDE3]/70 max-w-[160px]">{e.product_name || e.product_slug || "—"}</td>
                      <td className="px-4 py-4 text-xs text-[#F2EDE3]/70">{e.segment || "—"}</td>
                      <td className="px-4 py-4 text-xs text-[#F2EDE3]/70 whitespace-nowrap">{e.budget || "—"}</td>
                      <td className="px-4 py-4 text-xs text-[#F2EDE3]/70 whitespace-nowrap">{e.timeline || "—"}</td>
                      <td className="px-4 py-4 text-xs text-[#F2EDE3]/60 max-w-[220px]">
                        <span className="line-clamp-3" title={e.message}>{e.message}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <table className="w-full text-left text-sm min-w-[720px]">
                <thead>
                  <tr className="border-b border-[#D9A441]/15 text-[#D9A441]">
                    {["Date", "Name", "Actions", "Email", "Phone", "Interest"].map((h) => (
                      <th key={h} className="eyebrow !text-[9px] px-4 py-4 whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {requests.length === 0 && (
                    <tr><td colSpan={6} className="px-4 py-10 text-center text-[#F2EDE3]/40">No catalogue requests yet.</td></tr>
                  )}
                  {pageItems.map((r) => (
                    <tr key={r.id} data-testid={`admin-request-row-${r.id}`} className="border-b border-white/5 hover:bg-white/[0.025]">
                      <td className="px-4 py-4 whitespace-nowrap text-[#F2EDE3]/55 text-xs">{fmtDate(r.created_at)}</td>
                      <td className="px-4 py-4 font-semibold whitespace-nowrap">{r.name}</td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => setViewItem({ kind: "request", data: r })}
                            data-testid={`admin-view-request-${r.id}`}
                            title="View full details"
                            className="p-2 rounded-sm border border-[#D9A441]/40 text-[#D9A441] hover:bg-[#D9A441]/10 transition-colors"
                          >
                            <Eye className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => toggleRequestStatus(r)}
                            data-testid={`admin-request-status-toggle-${r.id}`}
                            className={`eyebrow !text-[9px] px-3 py-1.5 rounded-sm border transition-colors whitespace-nowrap ${
                              r.status === "contacted"
                                ? "border-[#4ade80]/40 text-[#4ade80]"
                                : "border-[#D9A441] bg-[#D9A441]/10 text-[#D9A441]"
                            }`}
                          >
                            {r.status === "contacted" ? "Sent ✓" : "Mark sent"}
                          </button>
                          <button
                            onClick={() => removeRequest(r)}
                            data-testid={`admin-delete-request-${r.id}`}
                            title="Delete request"
                            className="p-2 rounded-sm border border-white/10 text-[#F2EDE3]/50 hover:border-red-500/60 hover:text-red-400 transition-colors"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                      <td className="px-4 py-4 text-xs"><a href={`mailto:${r.email}`} className="text-[#D9A441] hover:underline">{r.email}</a></td>
                      <td className="px-4 py-4 text-xs text-[#F2EDE3]/70">{r.phone || "—"}</td>
                      <td className="px-4 py-4 text-xs text-[#F2EDE3]/70 max-w-[280px]">{r.interest || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Pagination */}
          {activeList.length > 0 && (
            <div data-testid="admin-pagination" className="mt-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs text-[#F2EDE3]/60">
              <div>
                Showing <span className="text-[#F2EDE3]">{pageStart + 1}</span>–<span className="text-[#F2EDE3]">{Math.min(pageStart + PAGE_SIZE, activeList.length)}</span> of <span className="text-[#F2EDE3]">{activeList.length}</span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  data-testid="admin-page-prev"
                  className="p-2 rounded-sm border border-white/10 text-[#F2EDE3]/70 hover:border-[#D9A441]/60 hover:text-[#D9A441] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft className="w-3.5 h-3.5" />
                </button>
                <span data-testid="admin-page-indicator" className="eyebrow !text-[10px] px-3 py-2 rounded-sm border border-[#D9A441]/30 text-[#D9A441]">
                  Page {currentPage} / {totalPages}
                </span>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  data-testid="admin-page-next"
                  className="p-2 rounded-sm border border-white/10 text-[#F2EDE3]/70 hover:border-[#D9A441]/60 hover:text-[#D9A441] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          )}
        </main>
      )}

      {/* View details modal */}
      {viewItem && (
        <div
          data-testid="admin-view-modal"
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-start md:items-center justify-center p-4 md:p-8 overflow-y-auto"
          onClick={() => setViewItem(null)}
        >
          <div
            className="panel w-full max-w-2xl relative"
            onClick={(ev) => ev.stopPropagation()}
          >
            <button
              onClick={() => setViewItem(null)}
              data-testid="admin-view-close"
              className="absolute top-4 right-4 p-2 rounded-sm border border-white/10 text-[#F2EDE3]/60 hover:border-[#D9A441] hover:text-[#D9A441] transition-colors"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>
            <div className="p-6 md:p-8">
              <div className="eyebrow text-[#D9A441] mb-1">
                {viewItem.kind === "enquiry" ? "Enquiry" : "Catalogue Request"} · {fmtDate(viewItem.data.created_at)}
              </div>
              <h3 className="font-display text-2xl md:text-3xl text-[#F2EDE3] mb-6">{viewItem.data.name}</h3>

              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 text-sm">
                <div>
                  <dt className="eyebrow !text-[9px] text-[#F2EDE3]/50 mb-1">Email</dt>
                  <dd><a href={`mailto:${viewItem.data.email}`} className="text-[#D9A441] hover:underline break-all">{viewItem.data.email}</a></dd>
                </div>
                <div>
                  <dt className="eyebrow !text-[9px] text-[#F2EDE3]/50 mb-1">Phone</dt>
                  <dd className="text-[#F2EDE3]/80">{viewItem.data.phone ? <a href={`tel:${viewItem.data.phone}`} className="hover:text-[#D9A441]">{viewItem.data.phone}</a> : "—"}</dd>
                </div>

                {viewItem.kind === "enquiry" ? (
                  <>
                    <div>
                      <dt className="eyebrow !text-[9px] text-[#F2EDE3]/50 mb-1">Type</dt>
                      <dd className="text-[#F2EDE3]/80">{viewItem.data.enquiry_type || "—"}</dd>
                    </div>
                    <div>
                      <dt className="eyebrow !text-[9px] text-[#F2EDE3]/50 mb-1">Segment</dt>
                      <dd className="text-[#F2EDE3]/80">{viewItem.data.segment || "—"}</dd>
                    </div>
                    <div>
                      <dt className="eyebrow !text-[9px] text-[#F2EDE3]/50 mb-1">Product / Interest</dt>
                      <dd className="text-[#F2EDE3]/80">{viewItem.data.product_name || viewItem.data.product_slug || "—"}</dd>
                    </div>
                    <div>
                      <dt className="eyebrow !text-[9px] text-[#F2EDE3]/50 mb-1">Budget</dt>
                      <dd className="text-[#F2EDE3]/80">{viewItem.data.budget || "—"}</dd>
                    </div>
                    <div>
                      <dt className="eyebrow !text-[9px] text-[#F2EDE3]/50 mb-1">Timeline</dt>
                      <dd className="text-[#F2EDE3]/80">{viewItem.data.timeline || "—"}</dd>
                    </div>
                    <div>
                      <dt className="eyebrow !text-[9px] text-[#F2EDE3]/50 mb-1">Status</dt>
                      <dd className={viewItem.data.status === "contacted" ? "text-[#4ade80]" : "text-[#D9A441]"}>
                        {viewItem.data.status === "contacted" ? "Contacted ✓" : "New"}
                      </dd>
                    </div>
                    <div className="sm:col-span-2">
                      <dt className="eyebrow !text-[9px] text-[#F2EDE3]/50 mb-1">Message</dt>
                      <dd className="text-[#F2EDE3]/80 whitespace-pre-wrap leading-relaxed">{viewItem.data.message || "—"}</dd>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="sm:col-span-2">
                      <dt className="eyebrow !text-[9px] text-[#F2EDE3]/50 mb-1">Interest</dt>
                      <dd className="text-[#F2EDE3]/80 whitespace-pre-wrap leading-relaxed">{viewItem.data.interest || "—"}</dd>
                    </div>
                    <div>
                      <dt className="eyebrow !text-[9px] text-[#F2EDE3]/50 mb-1">Status</dt>
                      <dd className={viewItem.data.status === "contacted" ? "text-[#4ade80]" : "text-[#D9A441]"}>
                        {viewItem.data.status === "contacted" ? "Sent ✓" : "New"}
                      </dd>
                    </div>
                  </>
                )}
              </dl>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
